import { useMemo, useState, type JSX } from 'react';

import { shaktiApi, ApiError } from '../api/client';

import { useProjectScopedEffect } from '../stores/project';
interface AuditEntry {
  id: string;
  packet_id: string | null;
  actor: string;
  event_type: string;
  timestamp: string;
  hash: string;
  prev_hash: string | null;
  payload: Record<string, unknown>;
}

/**
 * K5.2 (round-5) — audit trail + SOC 2 evidence bundle export.
 *
 * Serves the Meera persona (phase-neg1 journey_evidence_review):
 * requirement lookup, lineage inspection, and signed-bundle export.
 * Round-5 K5.1 ships the server-side /v1/audit/trail + /v1/audit/export
 * routes. This page is the dashboard consumer.
 */
export function AuditRoute(): JSX.Element {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [chainVerified, setChainVerified] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterPacketId, setFilterPacketId] = useState('');
  const [exporting, setExporting] = useState<string | null>(null);

  // N5.5 / W1.1 (v1.1) — project-scoped; aborts in-flight on switch
  // so a stale response from a prior project can never paint.
  useProjectScopedEffect(
    (signal) => {
      setLoading(true);
      setError(null);
      shaktiApi
        .getAuditTrail(
          filterPacketId ? { packet_id: filterPacketId } : {},
          { signal },
        )
        .then((res) => {
          if (signal.aborted) return;
          setEntries(res.entries);
          setChainVerified(res.chain_verified);
        })
        .catch((e: unknown) => {
          if (signal.aborted) return;
          setError(friendlyError(e));
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [filterPacketId],
  );

  async function exportBundle(packetId: string): Promise<void> {
    setExporting(packetId);
    try {
      const blob = await shaktiApi.exportAuditBundle(packetId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shakti-audit-bundle-${packetId}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setExporting(null);
    }
  }

  const packetIds = useMemo(() => {
    const seen = new Set<string>();
    for (const e of entries) {
      if (e.packet_id) seen.add(e.packet_id);
    }
    return [...seen];
  }, [entries]);

  return (
    <section aria-labelledby="audit-title" className="mx-auto flex max-w-6xl flex-col gap-6">
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/audit
        </span>
        <h1 id="audit-title" className="text-2xl font-semibold text-fg-primary">
          Audit trail
        </h1>
        <p className="text-sm text-fg-secondary">
          Merkle-chained record of every governed decision. Export a signed evidence bundle for
          your SOC 2 Type II auditor in one click.
        </p>
      </header>

      <section className="shakti-card flex items-center gap-3 p-4" aria-label="Chain status">
        <span
          className={`inline-block h-2 w-2 rounded-full ${
            chainVerified === null
              ? 'bg-fg-muted'
              : chainVerified
                ? 'bg-status-active'
                : 'bg-status-error'
          }`}
          aria-hidden
        />
        <span className="text-sm">
          {chainVerified === null
            ? 'Chain integrity: checking…'
            : chainVerified
              ? 'Chain integrity verified — every entry hashes correctly against its predecessor.'
              : 'Chain integrity BROKEN — a hash mismatch was detected. Contact your administrator.'}
        </span>
      </section>

      <section className="shakti-card flex flex-col gap-3 p-6">
        <header className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Entries</h2>
          <label className="text-xs">
            Filter by packet
            <select
              value={filterPacketId}
              onChange={(e) => setFilterPacketId(e.target.value)}
              className="ml-2 rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-xs"
              data-testid="audit-packet-filter"
            >
              <option value="">All</option>
              {packetIds.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </label>
        </header>

        {error ? (
          <div
            role="alert"
            className="rounded-sm border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
          >
            {error}
          </div>
        ) : null}

        {loading ? (
          <p className="text-xs text-fg-muted">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-fg-secondary">No audit entries recorded yet.</p>
        ) : (
          <ol className="flex flex-col gap-2" data-testid="audit-entries">
            {entries.map((e) => (
              <li
                key={e.id}
                className="flex gap-3 rounded-sm border border-border-subtle bg-bg-surface p-3"
              >
                <div className="flex flex-col gap-0.5 text-2xs text-fg-muted">
                  <span className="shakti-mono">{formatDate(e.timestamp)}</span>
                  <span title={e.hash} className="shakti-mono">
                    hash {e.hash.slice(0, 10)}…
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-1 text-xs">
                  <span>
                    <strong>{e.event_type}</strong> by{' '}
                    <span className="shakti-mono">{e.actor}</span>
                  </span>
                  {e.packet_id ? (
                    <span className="text-2xs text-fg-muted">
                      packet {e.packet_id.slice(0, 8)}
                    </span>
                  ) : null}
                </div>
                {e.packet_id ? (
                  <button
                    type="button"
                    onClick={() => void exportBundle(e.packet_id as string)}
                    disabled={exporting === e.packet_id}
                    className="self-center rounded-sm border border-border-subtle bg-bg-surface-2 px-3 py-1 text-2xs disabled:opacity-50"
                    data-testid={`export-bundle-${e.packet_id}`}
                  >
                    {exporting === e.packet_id ? 'Exporting…' : 'Export evidence bundle'}
                  </button>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </section>
    </section>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    return `${e.body?.error ?? e.message} (${e.status})`;
  }
  if (e instanceof Error) return e.message;
  return 'Unexpected error';
}
